'use client';

import { useRouter } from 'next/navigation';
import { useMemo, useEffect, ReactNode } from 'react';
import {
  useUserInfoContext,
  PageSkeletonWrapper,
} from '@bts-web/core-features/common';
import { getAppropriatenessRedirectUrl } from '../../utils/getAppropriatenessRedirectUrl';

interface AppropriatenessCheckGuardProps {
  assetType: string;
  operation?: string;
  assetId?: string;
  searchParams?: Record<string, string>;
  children: ReactNode;
  skipCheckAppropriateness?: boolean;
}

/**
 * Client-side component that checks if appropriateness test is required
 * for security assets (ETF, ETC, STOCK) during trading operations.
 *
 *@example
 * ```tsx
 * <AppropriatenessCheckGuard
 *   assetType="StockAsset"
 *   operation="buy"
 *   assetId="123"
 *   searchParams={{ swapWith: "456" }}
 * >
 *   <TradeForm />
 * </AppropriatenessCheckGuard>
 * ```
 */
export const AppropriatenessCheckGuard = ({
  assetType,
  operation,
  assetId,
  searchParams,
  children,
  skipCheckAppropriateness,
}: AppropriatenessCheckGuardProps) => {
  const router = useRouter();

  const { data: userData } = useUserInfoContext();

  const redirectUrl = useMemo(() => {
    if (skipCheckAppropriateness || !userData) {
      return null;
    }

    return getAppropriatenessRedirectUrl({
      assetType,
      operation,
      assetId,
      searchParams,
      appropriatenessStatus: {
        appropriatenessRequiresTestCompletion:
          userData.appropriatenessRequiresTestCompletion,
        appropriatenessRequiresTestResubmission:
          userData.appropriatenessRequiresTestResubmission,
        appropriatenessComplexProducts: userData.appropriatenessComplexProducts,
      },
    });
  }, [
    skipCheckAppropriateness,
    userData,
    assetType,
    operation,
    assetId,
    searchParams,
  ]);

  useEffect(() => {
    if (redirectUrl) {
      router.push(redirectUrl);
    }
  }, [redirectUrl, router]);

  if (redirectUrl) {
    return <PageSkeletonWrapper />;
  }

  return <>{children}</>;
};
